import React, { useState, useRef, useEffect } from 'react';
import { 
  Upload, 
  X, 
  FileText, 
  Plus, 
  Search, 
  Filter,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Image as ImageIcon,
  Trash2,
  ExternalLink,
  Calendar,
  ChevronDown,
  Trash,
  MoreHorizontal,
  RotateCcw,
  XCircle,
  Loader2,
  CreditCard
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { notify } from '../services/notify';
import { extractExpenseDetails } from '../services/geminiService';
import { collection, 
  onSnapshot, 
  query, 
  orderBy,
  where,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  getDocs,
  arrayUnion } from '../firebase';
import { db, auth, handleFirestoreError, OperationType, storage, ref, uploadBytes, getDownloadURL } from '../firebase';

import { SearchableSelect } from './ui/SearchableSelect';
import EntityActionLog from './EntityActionLog';

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitAmount: number;
  discountPercent: number;
  account: string;
  verified: boolean;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  account: string;
  reference: string;
}

interface ExpenseSubmission {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  vendor: string;
  category: string;
  attachments: string[];
  extractedContactName?: string;
  submittedBy?: string;
  userId?: string;
  userName?: string;
  createdAt?: string;
  lineItems?: LineItem[];
  payments?: Payment[];
  paymentReceived?: number;
}

interface FileDetail {
  id: string;
  file: File;
  description: string;
  paidFrom: string;
  selected: boolean;
}

export default function ExpenseSubmissionsView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState<1 | 2>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [submittedByFilter, setSubmittedByFilter] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileDetail[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ExpenseSubmission | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    accountId: '',
    method: 'Bank Transfer',
    reference: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<{ id: string; name: string }[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<{ id: string; name: string }[]>([]);
  const [contacts, setContacts] = useState<{ id: string; name: string; type?: string }[]>([]);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [submissions, setSubmissions] = useState<ExpenseSubmission[]>([]);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const localToken = getLocalAuthToken();
    if (localToken) {
      let cancelled = false;
      void Promise.all([
        fetch(`/api/organizations/${orgId}/expense-submissions`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/contacts`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/settings`, { headers: { Authorization: `Bearer ${localToken}` } })
      ])
        .then(async responses => {
          if (responses.some(response => !response.ok)) throw new Error('Failed to load local expense data');
          return Promise.all(responses.map(response => response.json()));
        })
        .then(([localSubmissions, localBanks, localAccounts, localContacts, localSettings]) => {
          if (cancelled) return;
          setSubmissions(Array.isArray(localSubmissions) ? localSubmissions : []);
          setBankAccounts((Array.isArray(localBanks) ? localBanks : []).map(account => ({ id: account.id, name: account.accountName })));
          setCoaAccounts((Array.isArray(localAccounts) ? localAccounts : [])
            .filter(account => account.status === 'active')
            .map(account => ({ id: account.id, name: account.name })));
          setContacts((Array.isArray(localContacts) ? localContacts : []).map(contact => ({ id: contact.id, name: contact.name, type: contact.type })));
          setOrgSettings({ ...(localSettings?.data || {}), ...(localSettings || {}) });
          setIsLoadingSubmissions(false);
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading expense submissions:', error);
            setSubmissions([]);
            setIsLoadingSubmissions(false);
          }
        });
      return () => { cancelled = true; };
    }

    const submissionsQ = query(
      collection(db, 'expense_submissions'), 
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );
    const unsubscribeSubmissions = onSnapshot(submissionsQ, (snapshot) => {
      setSubmissions(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ExpenseSubmission)));
      setIsLoadingSubmissions(false);
    }, (error) => {
      console.error("Error fetching submissions:", error);
      setIsLoadingSubmissions(false);
    });

    const bankQ = query(
      collection(db, 'bank_accounts'), 
      where('orgId', '==', orgId),
      orderBy('accountName', 'asc')
    );
    const unsubscribeBank = onSnapshot(bankQ, (snapshot) => {
      setBankAccounts(snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().accountName
      })));
    });

    const coaQ = query(
      collection(db, 'chart_of_accounts'), 
      where('orgId', '==', orgId),
      where('status', '==', 'active'),
      orderBy('name', 'asc')
    );
    const unsubscribeCoa = onSnapshot(coaQ, (snapshot) => {
      setCoaAccounts(snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as any))
        .filter(acc => acc.enablePayments === true)
        .map(acc => ({ id: acc.id, name: acc.name }))
      );
    });

    const contactsQ = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId),
      orderBy('name', 'asc')
    );
    const unsubscribeContacts = onSnapshot(contactsQ, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        type: doc.data().type
      })));
    });

    const unsubscribeSettings = onSnapshot(doc(db, 'settings', orgId), (snapshot) => {
      if (snapshot.exists()) setOrgSettings(snapshot.data());
    });

    return () => {
      unsubscribeBank();
      unsubscribeCoa();
      unsubscribeContacts();
      unsubscribeSettings();
    };
  }, []);

  const allAccounts = [...bankAccounts, ...coaAccounts].sort((a, b) => a.name.localeCompare(b.name));

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const uploadReceipt = async (file: File) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    const targetPath = `expenses/${orgId}/${Date.now()}_${file.name}`;

    if (token) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', targetPath);

        const response = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Could not upload receipt');

        const uploadedFile = result.file || result;
        const url = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
        if (!url) throw new Error('Receipt upload did not return a file URL');
        return url as string;
      } catch (error) {
        console.error('Local API receipt upload failed:', error);
        throw error;
      }
    }

const fileRef = ref(storage, targetPath);
    const snapshot = await uploadBytes(fileRef, file);
    return getDownloadURL(snapshot.ref);
  };

  const handleUpload = async () => {
    const filesToUpload = selectedFiles.filter(f => f.selected);
    if (filesToUpload.length === 0) return;

    setIsProcessing(true);
    const toastId = notify.loading(`AI is extracting information from ${filesToUpload.length} bill${filesToUpload.length !== 1 ? 's' : ''}...`);

    try {
      const results = await Promise.all(
        filesToUpload.map(async (fileDetail) => {
          try {
            const base64 = await fileToBase64(fileDetail.file);
            const extracted = await extractExpenseDetails(base64, fileDetail.file.type);
            
            const downloadUrl = await uploadReceipt(fileDetail.file);

            return {
              fileId: fileDetail.id,
              description: fileDetail.description,
              paidFrom: fileDetail.paidFrom,
              extracted,
              objectUrl: downloadUrl // Now it's a real persistent URL
            };
          } catch (err) {
            console.error(`Failed to process ${fileDetail.file.name}:`, err);
            
            // Still try to upload even if AI extraction failed
            let downloadUrl = '';
            try {
              downloadUrl = await uploadReceipt(fileDetail.file);
            } catch (uploadErr) {
              console.error("Backup upload failed:", uploadErr);
              downloadUrl = URL.createObjectURL(fileDetail.file); // Fallback to blob if even storage fails
            }

            return {
              fileId: fileDetail.id,
              description: fileDetail.description,
              paidFrom: fileDetail.paidFrom,
              extracted: null,
              error: true,
              objectUrl: downloadUrl
            };
          }
        })
      );

      console.log('AI Extraction Results:', results);
      
      const newSubmissions = await Promise.all(results.map(async (res) => {
        const extracted = res.extracted || {};
        const lineItems: LineItem[] = (extracted.lineItems || []).map((item: any) => ({
          id: Math.random().toString(36).substring(7),
          description: item.description || 'Line Item',
          quantity: parseFloat(item.quantity) || 1,
          unitAmount: parseFloat(item.unitPrice) || 0,
          discountPercent: 0,
          account: '300 - Purchases',
          verified: false
        }));

        const submissionData = {
          orgId,
          date: extracted.date || new Date().toISOString().split('T')[0],
          description: res.description || extracted.supplier || 'Expense Bill',
          amount: parseFloat(extracted.totalAmount) || 0,
          status: 'pending',
          vendor: extracted.supplier || 'Unknown Vendor',
          category: 'Purchases',
          attachments: [res.objectUrl],
          extractedContactName: extracted.supplier || '',
          submittedBy: 'Abdallah Mohamed',
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid,
          lineItems: lineItems.length > 0 ? lineItems : [
            {
              id: '1',
              description: res.description || extracted.supplier || 'Expense Bill',
              quantity: 1,
              unitAmount: parseFloat(extracted.totalAmount) || 0,
              discountPercent: 0,
              account: '300 - Purchases',
              verified: false
            }
          ],
          payments: []
        };

        const localToken = getLocalAuthToken();
        if (localToken) {
          const response = await fetch(`/api/organizations/${orgId}/expense-submissions`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...submissionData, createdAt: undefined, createdBy: undefined })
          });
          const saved = await response.json().catch(() => null);
          if (!response.ok) throw new Error(saved?.error || 'Could not create expense submission');
          return saved as ExpenseSubmission;
        }
        const docRef = await addDoc(collection(db, 'expense_submissions'), submissionData);
        return { id: docRef.id, ...submissionData };
      }));

      if (getLocalAuthToken()) {
        setSubmissions(current => [...(newSubmissions as ExpenseSubmission[]), ...current]);
      }
      notify.confirm(`Successfully processed ${newSubmissions.length} expense bill${newSubmissions.length !== 1 ? 's' : ''}!`, { id: toastId });
      closeUploadModal();
    } catch (error) {
      console.error('Upload failed:', error);
      notify.error('Failed to process some bills. Please try again.', { id: toastId });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files) as File[];
      
      // Limit to 100 files
      if (selectedFiles.length + newFiles.length > 100) {
        notify.error('You can only upload up to 100 images at once.');
        return;
      }

      // Validate file types and sizes
      const validFiles = newFiles.filter((file: File) => {
        const isValidType = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type);
        const isValidSize = file.size <= 6 * 1024 * 1024; // 6MB
        
        if (!isValidType) notify.error(`${file.name} is not a supported image format.`);
        if (!isValidSize) notify.error(`${file.name} exceeds the 6MB size limit.`);
        
        return isValidType && isValidSize;
      });

      const newFileDetails: FileDetail[] = validFiles.map(file => ({
        id: Math.random().toString(36).substring(7),
        file,
        description: '',
        paidFrom: '',
        selected: true
      }));

      setSelectedFiles(prev => [...prev, ...newFileDetails]);
    }
  };

  const removeFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  const updateFileDetail = (id: string, updates: Partial<FileDetail>) => {
    setSelectedFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const toggleSelectAll = () => {
    const allSelected = selectedFiles.every(f => f.selected);
    setSelectedFiles(prev => prev.map(f => ({ ...f, selected: !allSelected })));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleRecordPayment = async () => {
    if (!selectedSubmission || isSaving || !paymentData.accountId) {
      if (!paymentData.accountId) notify.error("Please select a payment account");
      return;
    }
    setIsSaving(true);
    try {
      const currentTotalPaid = selectedSubmission.paymentReceived || 0;
      const newTotalPaid = currentTotalPaid + paymentData.amount;
      const isFullyPaid = newTotalPaid >= selectedSubmission.amount;
      const newStatus = isFullyPaid ? 'paid' : 'approved';

      const actionEntry = {
        action: 'Payment Recorded',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: `Payment of ${paymentData.amount.toLocaleString()} recorded via ${paymentData.method} on ${paymentData.date}. Ref: ${paymentData.reference}`
      };

      const localToken = getLocalAuthToken();
      let updatedSubmission: ExpenseSubmission;
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/expense-submissions/${selectedSubmission.id}/payments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData)
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || 'Could not record expense payment');
        updatedSubmission = payload.submission;
      } else {
        await updateDoc(doc(db, 'expense_submissions', selectedSubmission.id), {
          status: newStatus,
          paymentReceived: newTotalPaid,
          updatedAt: serverTimestamp(),
          actionLog: arrayUnion(actionEntry)
        });

        // Create a transaction record
        await addDoc(collection(db, 'transactions'), {
          orgId,
          date: paymentData.date,
          description: `Expense Reimbursement: ${selectedSubmission.description}`,
          category: 'Expense',
          amount: -paymentData.amount,
          status: 'Completed',
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp(),
          paymentMethod: paymentData.method,
          reference: paymentData.reference,
          contactId: selectedSubmission.userId, // Reimbursement to user
          contactName: selectedSubmission.userName,
          accountId: paymentData.accountId,
          submissionId: selectedSubmission.id
        });
        updatedSubmission = { ...selectedSubmission, status: newStatus, paymentReceived: newTotalPaid };
      }

      setSubmissions(current => current.map(submission => submission.id === updatedSubmission.id ? updatedSubmission : submission));
      setSelectedSubmission(updatedSubmission);
      setIsPaymentModalOpen(false);
      notify.confirm(isFullyPaid ? "Expense fully paid" : "Partial payment recorded");
    } catch (error) {
      console.error("Error recording payment:", error);
      handleFirestoreError(error, OperationType.UPDATE, 'expense_submissions');
      notify.error("Failed to record payment");
    } finally {
      setIsSaving(false);
    }
  };
  const handleSubmissionStatus = async (status: 'approved' | 'rejected') => {
    if (!selectedSubmission) return;
    try {
      const localToken = getLocalAuthToken();
      let updatedSubmission: ExpenseSubmission;
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/expense-submissions/${selectedSubmission.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || 'Could not update expense submission');
        updatedSubmission = payload;
      } else {
        await updateDoc(doc(db, 'expense_submissions', selectedSubmission.id), {
          status,
          updatedAt: serverTimestamp()
        });
        updatedSubmission = { ...selectedSubmission, status };
      }
      setSubmissions(current => current.map(submission => submission.id === updatedSubmission.id ? updatedSubmission : submission));
      setSelectedSubmission(updatedSubmission);
      setIsDetailModalOpen(false);
      notify.confirm(status === 'approved' ? 'Submission approved' : 'Submission rejected');
    } catch (error) {
      console.error(`Error ${status === 'approved' ? 'approving' : 'rejecting'} submission:`, error);
      notify.error(`Failed to ${status === 'approved' ? 'approve' : 'reject'} submission`);
    }
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setUploadStep(1);
    setSelectedFiles([]);
  };

  const filteredSubmissions = submissions.filter(s => {
    const matchesSearch = s.description.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         s.vendor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || s.status === filter;
    
    const matchesDate = (!startDate || s.date >= startDate) && 
                        (!endDate || s.date <= endDate);
    
    const matchesAmount = (!minAmount || s.amount >= parseFloat(minAmount)) && 
                          (!maxAmount || s.amount <= parseFloat(maxAmount));
    
    const matchesCategory = !categoryFilter || s.category.toLowerCase().includes(categoryFilter.toLowerCase());
    const matchesUser = !submittedByFilter || (s.userName || '').toLowerCase().includes(submittedByFilter.toLowerCase());

    return matchesSearch && matchesFilter && matchesDate && matchesAmount && matchesCategory && matchesUser;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expense Submissions</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Review and approve employee expense claims</p>
        </div>
        <button 
          onClick={() => setIsUploadModalOpen(true)}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>Upload Expense Bills</span>
        </button>
      </div>

      {/* Header Actions */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
            {['all', 'pending', 'approved', 'rejected', 'paid'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f as any)}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-bold capitalize transition-all whitespace-nowrap border shrink-0",
                  filter === f 
                    ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                    : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
                )}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
              <input 
                type="text" 
                placeholder="Search submissions..." 
                className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm font-bold"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button 
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={cn(
                "p-2.5 rounded-full border transition-all shadow-sm",
                showAdvancedFilters || startDate || endDate || minAmount || maxAmount || categoryFilter || submittedByFilter
                  ? "bg-[#141414] text-white border-[#141414]" 
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white border border-[#141414]/10 p-4 rounded-2xl animate-in slide-in-from-top-2 shadow-sm">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Start Date</label>
              <input 
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">End Date</label>
              <input 
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Amount Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="number" 
                  placeholder="Min"
                  value={minAmount}
                  onChange={e => setMinAmount(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
                />
                <input 
                  type="number" 
                  placeholder="Max"
                  value={maxAmount}
                  onChange={e => setMaxAmount(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Category</label>
              <input 
                type="text" 
                placeholder="Category..."
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Submitted By</label>
              <input 
                type="text" 
                placeholder="User name..."
                value={submittedByFilter}
                onChange={e => setSubmittedByFilter(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 ring-gray-100"
              />
            </div>
            <div className="md:col-span-3 flex items-end">
              <button 
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setMinAmount('');
                  setMaxAmount('');
                  setCategoryFilter('');
                  setSubmittedByFilter('');
                  setSearchQuery('');
                }}
                className="px-6 py-2 bg-red-50 text-red-500 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-red-100 transition-colors ml-auto shadow-sm"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submissions List */}
      <div className="bg-white/50 border border-[#141414] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#141414]">
                <th className="p-4 text-xs font-bold uppercase opacity-50">Date</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50">Description</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50">Vendor</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50">Category</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50">Amount</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50">Status</th>
                <th className="p-4 text-xs font-bold uppercase opacity-50"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((s) => (
                <tr 
                  key={s.id} 
                  onClick={() => {
                    setSelectedSubmission(s);
                    setIsDetailModalOpen(true);
                  }}
                  className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-colors group cursor-pointer"
                >
                  <td className="p-4 text-sm font-medium">{s.date}</td>
                  <td className="p-4">
                    <div className="text-sm font-bold">{s.description}</div>
                    <div className="text-xs opacity-50 italic serif">{s.id}</div>
                  </td>
                  <td className="p-4 text-sm">{s.vendor}</td>
                  <td className="p-4 text-sm opacity-60">{s.category}</td>
                  <td className="p-4 text-sm font-bold">${s.amount.toFixed(2)}</td>
                  <td className="p-4">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-bold uppercase flex items-center gap-1 w-fit",
                      s.status === 'approved' && "bg-green-100 text-green-700",
                      s.status === 'pending' && "bg-yellow-100 text-yellow-700",
                      s.status === 'rejected' && "bg-red-100 text-red-700"
                    )}>
                      {s.status === 'approved' && <CheckCircle2 className="w-3 h-3" />}
                      {s.status === 'pending' && <Clock className="w-3 h-3" />}
                      {s.status === 'rejected' && <AlertCircle className="w-3 h-3" />}
                      {s.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors rounded-full opacity-0 group-hover:opacity-100">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {isUploadModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeUploadModal();
            }}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[32px] shadow-2xl border border-[#141414]"
            >
              <div className="p-6 flex items-center justify-between border-b border-[#141414]/10">
                <h2 className="text-lg font-bold italic serif">
                  {uploadStep === 1 ? 'Upload Expense Bills' : 'Specify Expense Bill Details'}
                </h2>
                <button 
                  onClick={closeUploadModal}
                  className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8 space-y-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {uploadStep === 1 ? (
                  <>
                    {/* Hidden File Input */}
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      className="hidden"
                    />

                    {/* Dropzone */}
                    <div 
                      onClick={triggerFileInput}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files);
                        const event = { target: { files } } as any;
                        handleFileSelect(event);
                      }}
                      className="border-2 border-dashed border-[#141414]/20 rounded-3xl p-12 flex flex-col items-center justify-center space-y-4 hover:border-[#141414]/40 transition-colors cursor-pointer group"
                    >
                      <div className="w-16 h-16 bg-[#141414]/5 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Upload className="w-8 h-8 opacity-50" />
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#141414]">Upload expense bill images</p>
                        <p className="text-sm opacity-50">or drag and drop</p>
                      </div>
                      <p className="text-xs opacity-40 font-medium uppercase tracking-wider">
                        PNG, JPG, GIF, WEBP up to 6MB each
                      </p>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold uppercase tracking-wider opacity-50">Selected Files ({selectedFiles.length})</h3>
                          <button 
                            onClick={() => setSelectedFiles([])}
                            className="text-xs font-bold text-red-500 hover:underline"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {selectedFiles.map((fileDetail) => (
                            <div key={fileDetail.id} className="relative group aspect-square bg-[#141414]/5 rounded-2xl overflow-hidden border border-[#141414]/10">
                              <img 
                                src={URL.createObjectURL(fileDetail.file)} 
                                alt={fileDetail.file.name}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile(fileDetail.id);
                                  }}
                                  className="p-2 bg-red-500 text-white rounded-full hover:scale-110 transition-transform"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                                <p className="text-[10px] text-white truncate font-medium">{fileDetail.file.name}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Guidelines */}
                    <div className="bg-[#141414]/5 border border-[#141414]/10 rounded-2xl p-6 space-y-3">
                      <h3 className="text-sm font-bold text-[#141414]">Upload Guidelines:</h3>
                      <ul className="space-y-2">
                        {[
                          'Only image files (PNG, JPG, GIF, WEBP) are accepted',
                          'Maximum file size: 6MB per image',
                          'You can upload multiple images at once',
                          'Clear, readable images work best for expense tracking'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#141414]/80">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-[#141414]/40 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <h3 className="text-lg font-bold">Specify Details for Each Expense Bill</h3>
                      <p className="text-sm opacity-60">Add descriptions and select the account. AI will automatically extract and process the expense details.</p>
                    </div>

                    {/* Select All Bar */}
                    <div className="flex items-center justify-between p-4 bg-[#141414]/5 border border-[#141414]/10 rounded-2xl">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={selectedFiles.length > 0 && selectedFiles.every(f => f.selected)}
                          onChange={toggleSelectAll}
                          className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                        />
                        <span className="text-sm font-bold">Select All ({selectedFiles.filter(f => f.selected).length} selected)</span>
                      </label>
                      <button 
                        onClick={triggerFileInput}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-[#141414]/10 rounded-xl text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add More Files
                      </button>
                    </div>

                    {/* File Details List */}
                    <div className="space-y-4">
                      {selectedFiles.map((fileDetail) => (
                        <div key={fileDetail.id} className="p-6 border border-[#141414]/10 rounded-2xl bg-white space-y-4 relative group">
                          <div className="flex gap-6">
                            <div className="flex items-start pt-1">
                              <input 
                                type="checkbox" 
                                checked={fileDetail.selected}
                                onChange={() => updateFileDetail(fileDetail.id, { selected: !fileDetail.selected })}
                                className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                              />
                            </div>
                            
                            <div className="w-24 h-24 rounded-xl overflow-hidden border border-[#141414]/10 shrink-0">
                              <img 
                                src={URL.createObjectURL(fileDetail.file)} 
                                alt={fileDetail.file.name}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            </div>

                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2 space-y-1.5">
                                <label className="text-xs font-bold uppercase opacity-50">Description</label>
                                <input 
                                  type="text" 
                                  value={fileDetail.description}
                                  onChange={(e) => updateFileDetail(fileDetail.id, { description: e.target.value })}
                                  className="w-full px-4 py-2.5 bg-[#141414]/5 border border-transparent rounded-xl focus:bg-white focus:border-[#141414]/20 outline-none transition-all text-sm"
                                  placeholder="Optional notes"
                                />
                              </div>
                              <div className="md:col-span-2 space-y-1.5">
                                <label className="text-xs font-bold uppercase opacity-50">Paid From</label>
                                <SearchableSelect 
                                  value={fileDetail.paidFrom}
                                  onChange={(e) => updateFileDetail(fileDetail.id, { paidFrom: e.target.value })}
                                  options={allAccounts.map(account => ({
                                    id: account.id,
                                    label: account.name
                                  }))}
                                  placeholder="Select account"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-[10px] opacity-40 font-medium">
                                  File: {fileDetail.file.name} ({(fileDetail.file.size / 1024).toFixed(2)} KB)
                                </p>
                              </div>
                            </div>

                            <button 
                              onClick={() => removeFile(fileDetail.id)}
                              className="absolute top-6 right-6 p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 bg-[#141414]/5 flex items-center justify-end gap-4">
                {uploadStep === 2 && (
                  <button 
                    onClick={() => setUploadStep(1)}
                    className="px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                  >
                    Back
                  </button>
                )}
                <button 
                  onClick={closeUploadModal}
                  className="px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                >
                  Cancel
                </button>
                {uploadStep === 1 ? (
                  <button 
                    onClick={() => selectedFiles.length > 0 && setUploadStep(2)}
                    disabled={selectedFiles.length === 0}
                    className={cn(
                      "bg-[#141414] text-[#E4E3E0] px-10 py-3 rounded-xl text-sm font-bold transition-all shadow-lg shadow-[#141414]/10",
                      selectedFiles.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
                    )}
                  >
                    Next
                  </button>
                ) : (
                  <button 
                    onClick={handleUpload}
                    disabled={selectedFiles.filter(f => f.selected).length === 0 || isProcessing}
                    className={cn(
                      "bg-[#141414] text-[#E4E3E0] px-10 py-3 rounded-xl text-sm font-bold transition-all shadow-lg shadow-[#141414]/10 flex items-center justify-center gap-2",
                      (selectedFiles.filter(f => f.selected).length === 0 || isProcessing) ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
                    )}
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Upload {selectedFiles.filter(f => f.selected).length} File{selectedFiles.filter(f => f.selected).length !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {isDetailModalOpen && selectedSubmission && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/60 backdrop-blur-md overflow-y-auto overscroll-contain py-8 px-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsDetailModalOpen(false);
            }}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.98, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 20 }}
              className="bg-[#F5F5F4] w-full max-w-4xl rounded-[32px] overflow-hidden shadow-2xl border border-[#141414] flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-[#141414]/10 sticky top-0 z-10">
                <h2 className="text-lg font-bold italic serif tracking-tight">Expense Submission Details</h2>
                <div className="flex items-center gap-4">
                  <span className={cn(
                    "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest",
                    selectedSubmission.status === 'approved' && "bg-green-500 text-white",
                    selectedSubmission.status === 'pending' && "bg-yellow-500 text-white",
                    selectedSubmission.status === 'rejected' && "bg-red-500 text-white"
                  )}>
                    {selectedSubmission.status}
                  </span>
                  <button 
                    onClick={() => setIsDetailModalOpen(false)}
                    className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Left Sidebar - Receipt Scan */}
                <div className="lg:w-[400px] bg-white border-r border-[#141414]/10 p-8 flex flex-col gap-4 overflow-y-auto">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] opacity-40">Receipt / Invoice Scan</h3>
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-[#141414]/10 bg-[#141414]/5 relative group">
                    {selectedSubmission.attachments[0] ? (
                      <img 
                        src={selectedSubmission.attachments[0]} 
                        alt="Receipt" 
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center opacity-20">
                        <ImageIcon className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button className="p-3 bg-white rounded-full hover:scale-110 transition-transform">
                        <Search className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 text-[#141414] font-bold text-sm hover:underline">
                    <ExternalLink className="w-4 h-4" />
                    Open in new tab
                  </button>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-[#F5F5F4]">
                  {/* Top Info Grid */}
                  {(() => {
                    const totalPaid = selectedSubmission.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
                    const amountDue = selectedSubmission.amount - totalPaid;
                    
                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 bg-white p-8 rounded-[32px] border border-[#141414]/5 shadow-sm">
                          <div className="space-y-6">
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Submission ID</label>
                              <p className="text-xs font-bold font-mono">{selectedSubmission.id}</p>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Extracted Contact Name</label>
                              <p className="text-xs font-bold">{selectedSubmission.extractedContactName || 'None'}</p>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 flex items-center gap-1">
                                Stored Contact <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <SearchableSelect 
                                    value={contacts.find(c => c.name === selectedSubmission.vendor)?.id || ''}
                                    onChange={(e) => {
                                      // Implementation for saving the link would go here
                                    }}
                                    options={contacts.filter(c => c.type === 'Supplier' || c.type === 'Both').map(c => ({
                                      id: c.id,
                                      label: c.name
                                    }))}
                                    placeholder="Select Contact"
                                  />
                                </div>
                                <button className="p-2 bg-[#141414]/5 rounded-xl hover:bg-[#141414]/10 transition-colors">
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 flex items-center gap-1">
                                Invoice Date <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input 
                                  type="date" 
                                  defaultValue={selectedSubmission.date}
                                  className="w-full px-4 py-2 bg-[#141414]/5 border border-transparent rounded-xl focus:bg-white focus:border-[#141414]/20 outline-none transition-all text-xs font-bold"
                                />
                                <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30 pointer-events-none" />
                              </div>
                            </div>
                          </div>

                          <div className="space-y-6">
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Submitted By</label>
                              <p className="text-xs font-bold">{selectedSubmission.submittedBy || 'Unknown'}</p>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Created At</label>
                              <p className="text-xs font-bold">
                                {selectedSubmission.createdAt ? new Date(selectedSubmission.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Financial Summary */}
                        <div className="grid grid-cols-3 gap-1 bg-white p-1 rounded-[32px] border border-[#141414]/5 shadow-sm overflow-hidden">
                          <div className="p-8 space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#141414]/60">Total Amount</p>
                            <p className="text-lg font-bold italic serif">{orgSettings?.currencySymbol || '$'} {selectedSubmission.amount.toFixed(2)}</p>
                          </div>
                          <div className="p-8 space-y-1 border-x border-[#141414]/5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-green-600">Total Paid</p>
                            <p className="text-lg font-bold italic serif">{orgSettings?.currencySymbol || '$'} {totalPaid.toFixed(2)}</p>
                          </div>
                          <div className="p-8 space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-600">Amount Due</p>
                            <p className={cn(
                              "text-lg font-bold italic serif",
                              amountDue > 0 ? "text-red-600" : "text-green-600"
                            )}>
                              {orgSettings?.currencySymbol || '$'} {amountDue.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Line Items Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold italic serif">Line Items</h3>
                      <button className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-2 rounded-xl text-sm font-bold hover:opacity-90 transition-colors shadow-lg shadow-[#141414]/10">
                        <Plus className="w-4 h-4" />
                        Add Line Item
                      </button>
                    </div>

                    <div className="bg-white rounded-[32px] border border-[#141414]/5 shadow-sm overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-[#141414]/5">
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Description</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Qty</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Unit Amount</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Disc %</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Total</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">Account</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Verified</th>
                            <th className="p-6 text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#141414]/5">
                          {selectedSubmission.lineItems?.map((item) => (
                            <tr key={item.id} className="group hover:bg-[#141414]/[0.02] transition-colors">
                              <td className="p-6">
                                <input 
                                  type="text" 
                                  defaultValue={item.description}
                                  className="w-full bg-transparent border-none outline-none text-xs font-medium"
                                />
                              </td>
                              <td className="p-6 text-center">
                                <input 
                                  type="number" 
                                  value={isNaN(item.quantity) ? '' : item.quantity}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    // Update logic would go here if this was fully editable
                                  }}
                                  className="w-16 bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold text-center outline-none"
                                />
                              </td>
                              <td className="p-6 text-center">
                                <input 
                                  type="number" 
                                  value={isNaN(item.unitAmount) ? '' : item.unitAmount}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    // Update logic would go here if this was fully editable
                                  }}
                                  className="w-20 bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold text-center outline-none"
                                />
                              </td>
                              <td className="p-6 text-center">
                                <input 
                                  type="number" 
                                  value={isNaN(item.discountPercent) ? '' : item.discountPercent}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    // Update logic would go here if this was fully editable
                                  }}
                                  className="w-16 bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold text-center outline-none"
                                />
                              </td>
                              <td className="p-6 text-center font-bold text-xs">
                                {orgSettings?.currencySymbol || '$'} {(item.quantity * item.unitAmount * (1 - item.discountPercent / 100)).toFixed(2)}
                              </td>
                              <td className="p-6">
                                <SearchableSelect 
                                  value={item.account}
                                  onChange={(e) => {
                                    // Implementation for updating line item account would go here
                                  }}
                                  options={allAccounts.map(acc => ({
                                    id: acc.name,
                                    label: acc.name
                                  }))}
                                  placeholder="Select Account"
                                />
                              </td>
                              <td className="p-6 text-center">
                                <input 
                                  type="checkbox" 
                                  defaultChecked={item.verified}
                                  className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                                />
                              </td>
                              <td className="p-6 text-center">
                                <button className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold italic serif">Payments</h3>
                        {selectedSubmission.status === 'approved' && (
                          <button 
                            onClick={() => {
                              const amountDue = selectedSubmission.amount - (selectedSubmission.paymentReceived || 0);
                              setPaymentData(prev => ({ ...prev, amount: amountDue }));
                              setIsPaymentModalOpen(true);
                            }}
                            className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-2 rounded-xl text-sm font-bold hover:opacity-90 transition-colors shadow-lg shadow-[#141414]/10"
                          >
                            <CreditCard className="w-4 h-4" />
                            Record Payment
                          </button>
                        )}
                      </div>

                      <div className="bg-white rounded-[32px] border border-[#141414]/5 shadow-sm p-12 text-center space-y-4">
                        {selectedSubmission.paymentReceived ? (
                          <div className="text-left space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="opacity-50">Total Amount:</span>
                              <span className="font-bold">${selectedSubmission.amount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="opacity-50">Total Paid:</span>
                              <span className="font-bold text-green-600">${selectedSubmission.paymentReceived.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm border-t pt-2">
                              <span className="opacity-50">Remaining:</span>
                              <span className="font-bold text-red-600">${(selectedSubmission.amount - selectedSubmission.paymentReceived).toFixed(2)}</span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm opacity-50 font-medium italic serif">No payments recorded yet.</p>
                        )}
                      </div>
                    </div>

                    <div className="rounded-[32px] border border-[#141414]/5 bg-white p-6 shadow-sm">
                      <EntityActionLog orgId={orgId} entityId={selectedSubmission.id} entityType="Expense Submission" />
                    </div>

                  {/* Action Buttons */}
                  <div className="pt-8 flex items-center justify-end gap-4">
                    <button 
                      onClick={() => setIsDetailModalOpen(false)}
                      className="px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                    >
                      Close
                    </button>
                    {selectedSubmission.status === 'pending' && (
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => void handleSubmissionStatus('rejected')}
                          className="px-8 py-3 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 transition-colors"
                        >
                          Reject
                        </button>
                        <button 
                          onClick={() => void handleSubmissionStatus('approved')}
                          className="bg-[#141414] text-[#E4E3E0] px-10 py-3 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity shadow-xl shadow-[#141414]/20"
                        >
                          Approve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Record Payment Modal */}
      <AnimatePresence>
        {isPaymentModalOpen && selectedSubmission && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsPaymentModalOpen(false);
            }}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl border border-[#141414]"
            >
              <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between">
                <h2 className="text-lg font-bold italic serif">Record Payment</h2>
                <button 
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Date *</label>
                    <input 
                      type="date" 
                      required
                      className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold text-sm"
                      value={paymentData.date}
                      onChange={(e) => setPaymentData({...paymentData, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Amount *</label>
                    <input 
                      type="number" 
                      required
                      step="0.01"
                      className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold text-sm"
                      value={isNaN(paymentData.amount) ? '' : paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Paid From *</label>
                  <SearchableSelect 
                    value={paymentData.accountId}
                    onChange={(e) => setPaymentData({...paymentData, accountId: e.target.value})}
                    options={bankAccounts.map(acc => ({
                      id: acc.id,
                      label: acc.name
                    }))}
                    placeholder="Select account..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Method *</label>
                  <SearchableSelect 
                    value={paymentData.method}
                    onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
                    options={[
                      { id: 'Bank Transfer', label: 'Bank Transfer' },
                      { id: 'Cash', label: 'Cash' },
                      { id: 'Credit Card', label: 'Credit Card' },
                      { id: 'Check', label: 'Check' }
                    ]}
                    placeholder="Select method..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Reference</label>
                  <input 
                    type="text" 
                    placeholder="Optional reference number"
                    className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold text-sm"
                    value={paymentData.reference}
                    onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                  />
                </div>
                <button 
                  onClick={handleRecordPayment}
                  disabled={isSaving}
                  className="w-full bg-[#141414] text-[#E4E3E0] py-4 rounded-2xl font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    'Record Payment'
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
